/**
 * BusinessEmailManagement.tsx — admin operations for the /email product.
 *
 * Backed by /api/admin/email/business-email/*. Surfaces:
 *   Tier 1 (read): subscription list, detail drawer, provisioning
 *                  timeline, current mailbox usage, latest domain health
 *   Tier 2 (action): suspend / reactivate / admin-adjust extras / resend
 *                    welcome / retry provisioning / force DNS recheck /
 *                    force state machine transition
 *   Tier 3 (config): tier config editor (base mailboxes, storage,
 *                    aliases, extras price, max extras) + aggregate stats
 *   Tier 4 (escape hatch): manual subscription creation + audit log
 *
 * Colour policy: logo palette only. Status conveyed by icon + yellow
 * intensity; no red/green here per memory.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../../config/environment';
import { fmtMb, fmtRelative } from '../email/emailApi';

interface Subscription {
  id: string;
  domain: string;
  package_name: string;
  status: 'pending_provisioning' | 'active' | 'suspended' | 'cancelled';
  billing_cycle: 'monthly' | 'yearly';
  base_mailboxes: number;
  extra_mailboxes: number;
  cap: number;
  base_price_monthly: string;
  extra_price_monthly: string;
  current_period_end: string | null;
  started_at: string | null;
  created_at: string;
  stripe_subscription_id: string | null;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_mail_domain_id: string | null;
  cmd_status: string | null;
  cmd_last_error: string | null;
  health_severity: 'ok' | 'warning' | 'critical' | null;
  health_reason: string | null;
  // Business Email v2 (mx1) fields — present when the sub has an mx1 row.
  mx1_status?: string | null;
  mx1_row_id?: string | null;
  mx1_last_error?: string | null;
  ses_identity_status?: string | null;
  welcome_email_sent_at?: string | null;
  suspended_reason?: string | null;
  order_id?: string | null;
}

interface Tier {
  id: string;
  name: string;
  description: string | null;
  price_monthly: string;
  price_yearly: string;
  status: string;
  mailboxes_per_subscription: number;
  storage_per_mailbox_gb: number;
  aliases_per_subscription: number;
  extra_mailbox_price_monthly: string;
  extra_mailbox_price_yearly: string;
  max_extra_mailboxes: number | null;
}

interface Stats {
  total_subscriptions: number;
  active_count: number;
  pending_count: number;
  suspended_count: number;
  cancelled_count: number;
  mrr: string;
  total_mailbox_seats: number;
  active_mail_domains: string;
  by_tier: Array<{ package_name: string; active: number; pending: number; seats: number }>;
}

const token = () => localStorage.getItem('token');
async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(getApiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(init.headers || {}),
    },
  });
  const body: any = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err: any = new Error(body?.error || body?.message || `Request failed (HTTP ${r.status})`);
    err.status = r.status;
    throw err;
  }
  return body as T;
}

/** Close-on-Escape for modals/drawers. */
function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

const statusChip = (s: Subscription['status']) => {
  // All-in-brand-palette chips (logo separation memo). Status conveyed
  // via icon + yellow intensity, not red/green.
  switch (s) {
    case 'active':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-500 text-primary-navy font-semibold">✓ Active</span>;
    case 'pending_provisioning':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-primary-navy">⏳ Provisioning</span>;
    case 'suspended':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-200 text-primary-navy border border-primary-navy">⚠ Suspended</span>;
    case 'cancelled':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">· Cancelled</span>;
  }
};

const healthChip = (sev: Subscription['health_severity'], reason?: string | null) => {
  if (!sev || sev === 'ok') return null;
  if (sev === 'critical')
    return <span className="ml-1 inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-primary-navy text-secondary-300" title={reason || 'Critical domain health'}>🚨 <span className="sr-only">critical</span></span>;
  return <span className="ml-1 inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-secondary-300 text-primary-navy" title={reason || 'Domain health warning'}>⚠ <span className="sr-only">warning</span></span>;
};

// ---- Stats header ----

const StatsHeader: React.FC<{ stats: Stats | null }> = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[
        { label: 'Active', value: stats.active_count, sub: `${stats.pending_count} provisioning · ${stats.suspended_count} suspended` },
        { label: 'MRR', value: `$${stats.mrr}`, sub: 'monthly recurring' },
        { label: 'Active seats', value: stats.total_mailbox_seats, sub: 'total mailboxes billed' },
        { label: 'Mail domains', value: stats.active_mail_domains, sub: 'business-email only' },
      ].map(s => (
        <div key={s.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="text-xs uppercase tracking-wider text-gray-500">{s.label}</div>
          <div className="mt-1 text-2xl font-bold text-primary-navy tabular-nums">{s.value}</div>
          <div className="text-xs text-gray-500 mt-1">{s.sub}</div>
        </div>
      ))}
    </div>
  );
};

// ---- Subscription detail drawer ----

const DetailDrawer: React.FC<{ subId: string | null; onClose: () => void; onChanged: () => void }> = ({ subId, onClose, onChanged }) => {
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  useEscape(onClose);

  const load = useCallback(async () => {
    if (!subId) return;
    try {
      const r = await api(`/admin/email/business-email/subscriptions/${subId}`);
      if (r.success) { setData(r.data); setLoadErr(null); }
      else setLoadErr(r.error || 'Failed to load subscription');
    } catch (e: any) {
      setLoadErr(e?.message || 'Failed to load subscription');
    }
  }, [subId]);

  useEffect(() => { load(); }, [load]);

  if (!subId) return null;
  const sub = data?.subscription;
  const cmd = data?.customer_mail_domain;
  const health = data?.latest_health;
  const events = data?.events || [];
  const mailboxes = data?.mailboxes || [];
  // Optional per-mailbox usage (v2): [{id,email,quota_mb,used_mb,used_pct,messages,last_login_at,...}]
  const usageRows: any[] = Array.isArray(data?.usage) ? data.usage : [];
  const usageByKey = new Map<string, any>();
  for (const u of usageRows) {
    if (u?.id !== undefined) usageByKey.set(`id:${u.id}`, u);
    if (u?.email) usageByKey.set(`email:${String(u.email).toLowerCase()}`, u);
  }
  const usageFor = (m: any) => usageByKey.get(`id:${m?.id}`) || usageByKey.get(`email:${String(m?.email || '').toLowerCase()}`) || null;
  const usageTotals = usageRows.reduce((a, u) => ({
    used: a.used + (Number(u.used_mb) || 0),
    quota: a.quota + (Number(u.quota_mb) || 0),
  }), { used: 0, quota: 0 });

  // v2 (mx1) rows get the v2 admin endpoints; v1 (customer_mail_domains)
  // rows fall back to the legacy ones only when there's a cmd id and no mx1 row.
  const mx1RowId: string | null = sub?.mx1_row_id || data?.mx1_mail_domain?.id || null;
  const isV2 = !!mx1RowId;
  const mx1Status: string | null = sub?.mx1_status || data?.mx1_mail_domain?.provisioning_status || null;
  const mx1LastError: string | null = sub?.mx1_last_error || data?.mx1_mail_domain?.last_error || null;
  const cmdLastError: string | null = cmd?.last_error || sub?.cmd_last_error || null;
  const sesStatus: string | null = sub?.ses_identity_status || data?.mx1_mail_domain?.ses_identity_status || cmd?.ses_identity_status || null;
  const welcomeSentAt: string | null = sub?.welcome_email_sent_at || data?.mx1_mail_domain?.welcome_email_sent_at || cmd?.welcome_email_sent_at || null;
  const suspendedReason: string | null = sub?.suspended_reason || null;

  const act = async (label: string, fn: () => Promise<any>) => {
    setBusy(label);
    setErr(null);
    try {
      const r = await fn();
      if (r && r.success === false) setErr(r.error || 'Failed');
      await load();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || 'Failed');
    } finally { setBusy(null); }
  };

  const suspend = () => {
    const reason = window.prompt(`Suspend ${sub.domain}?\nMail stops flowing and the customer sees the reason you enter here.\n\nReason (shown to customer):`, suspendedReason || '');
    if (reason === null) return;
    if (!window.confirm(`Confirm suspension of ${sub.domain}?`)) return;
    act('suspend', () =>
      api(`/admin/email/business-email/subscriptions/${subId}/suspend`, { method: 'POST', body: JSON.stringify({ reason: reason.trim() || undefined }) }));
  };

  const retry = () => {
    if (isV2) return act('retry', () => api(`/admin/email/business-email/subscriptions/${subId}/retry-v2`, { method: 'POST' }));
    if (sub.order_id) return act('retry', () => api(`/admin/email/orders/${sub.order_id}/provision`, { method: 'POST' }));
    return act('retry', () => Promise.resolve({ success: false, error: 'No mx1 row and no order id — nothing to retry.' }));
  };
  const recheck = () => {
    if (isV2) return act('recheck', () => api(`/admin/email/business-email/subscriptions/${subId}/recheck-v2`, { method: 'POST' }));
    return act('recheck', () => api(`/admin/email/dns/run-poller`, { method: 'POST' }));
  };
  const welcome = () => {
    if (isV2) return act('welcome', () => api(`/admin/email/business-email/subscriptions/${subId}/welcome-v2`, { method: 'POST', body: JSON.stringify({ resend: true }) }));
    if (cmd?.id) return act('welcome', () => api(`/admin/email/domains/${cmd.id}/send-welcome`, { method: 'POST', body: JSON.stringify({ resend: true }) }));
    return act('welcome', () => Promise.resolve({ success: false, error: 'No mail domain row — cannot send a welcome email.' }));
  };

  return (
    <div className="fixed inset-0 bg-primary-navy/40 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={sub ? `Subscription ${sub.domain}` : 'Subscription detail'}
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div>
            {sub ? (
              <>
                <div className="font-semibold text-primary-navy break-all">{sub.domain}</div>
                <div className="text-xs text-gray-500">{sub.package_name} · {sub.customer_email}</div>
              </>
            ) : <div className="text-sm text-gray-400">{loadErr ? 'Not loaded' : 'Loading…'}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2">×</button>
        </div>

        {!data && !loadErr && <div className="p-5 text-sm text-gray-500">Loading…</div>}
        {loadErr && (
          <div role="alert" className="m-5 p-3 bg-secondary-100 border border-secondary-500 rounded text-sm text-primary-navy">
            🚨 {loadErr} <button type="button" onClick={load} className="underline ml-2">Retry</button>
          </div>
        )}
        {err && <div role="alert" className="m-5 p-3 bg-secondary-100 border border-secondary-500 rounded text-sm text-primary-navy">🚨 {err}</div>}

        {data && sub && (
          <div className="p-5 space-y-5">
            {/* Status + actions */}
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                {statusChip(sub.status)}
                <span className="text-xs text-gray-500 font-mono">
                  {isV2 ? `mx1: ${mx1Status || '—'}` : `cmd: ${cmd?.provisioning_status || '—'}`}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-primary-navy/90 mb-2">
                <dt className="text-gray-500">Pipeline</dt>
                <dd>{isV2 ? `v2 (mx1 row ${mx1RowId})` : cmd?.id ? `v1 (cmd ${cmd.id})` : 'no mail-domain row'}</dd>
                <dt className="text-gray-500">SES identity</dt>
                <dd>{sesStatus || '—'}</dd>
                <dt className="text-gray-500">Welcome email</dt>
                <dd>{welcomeSentAt ? `sent ${fmtDateTime(welcomeSentAt)}` : 'not sent'}</dd>
                {sub.status === 'suspended' && (
                  <>
                    <dt className="text-gray-500">Suspended reason</dt>
                    <dd>{suspendedReason || '—'}</dd>
                  </>
                )}
              </dl>
              {isV2 && mx1LastError && (
                <div className="text-xs text-primary-navy/80 mb-2 break-words">⚠ mx1: {mx1LastError}</div>
              )}
              {!isV2 && cmdLastError && (
                <div className="text-xs text-primary-navy/80 mb-2 break-words">⚠ cmd: {cmdLastError}</div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {sub.status === 'active' && (
                  <button type="button" onClick={suspend} disabled={!!busy}
                    className="text-xs px-3 py-1.5 bg-primary-navy text-secondary-300 rounded border border-secondary-500 disabled:opacity-50">
                    {busy === 'suspend' ? '…' : 'Suspend'}
                  </button>
                )}
                {sub.status === 'suspended' && (
                  <button type="button" onClick={() => act('reactivate', () =>
                    api(`/admin/email/business-email/subscriptions/${subId}/reactivate`, { method: 'POST' })
                  )} disabled={!!busy}
                    className="text-xs px-3 py-1.5 bg-secondary-500 text-primary-navy font-medium rounded disabled:opacity-50">
                    {busy === 'reactivate' ? '…' : 'Reactivate'}
                  </button>
                )}
                <button type="button" onClick={retry} disabled={!!busy || (!isV2 && !sub.order_id)}
                  title={isV2 ? 'POST retry-v2' : sub.order_id ? 'Re-run order provisioning (v1)' : 'Nothing to retry'}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-primary-navy rounded disabled:opacity-50">
                  {busy === 'retry' ? '…' : 'Retry provisioning'}
                </button>
                <button type="button" onClick={recheck} disabled={!!busy}
                  title={isV2 ? 'POST recheck-v2' : 'Run the v1 DNS poller'}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-primary-navy rounded disabled:opacity-50">
                  {busy === 'recheck' ? '…' : 'DNS recheck'}
                </button>
                <button type="button" onClick={welcome} disabled={!!busy || (!isV2 && !cmd?.id)}
                  title={isV2 ? 'POST welcome-v2' : cmd?.id ? 'Resend v1 welcome email' : 'No mail domain row'}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-primary-navy rounded disabled:opacity-50">
                  {busy === 'welcome' ? '…' : welcomeSentAt ? 'Resend welcome' : 'Send welcome'}
                </button>
                <button type="button" onClick={async () => {
                  const v = window.prompt(`Set extra_mailboxes (current: ${sub.extra_mailboxes})\nThis is an ADMIN OVERRIDE — Stripe is NOT updated. Use for goodwill / corrections.`);
                  if (v === null) return;
                  const n = Number(v);
                  if (!Number.isInteger(n) || n < 0) { window.alert('must be non-negative integer'); return; }
                  await act('extras', () =>
                    api(`/admin/email/business-email/subscriptions/${subId}/extras`, {
                      method: 'POST', body: JSON.stringify({ extras: n }),
                    }));
                }} disabled={!!busy}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-primary-navy rounded disabled:opacity-50">
                  {busy === 'extras' ? '…' : 'Override extras'}
                </button>
              </div>
            </div>

            {/* Billing snapshot */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Billing</div>
              <div className="text-sm text-primary-navy space-y-1">
                <div>Base: ${sub.base_price_monthly}/mo × {sub.base_mailboxes} mailboxes</div>
                <div>Extras: ${sub.extra_price_monthly}/mo × {sub.extra_mailboxes}</div>
                <div className="font-semibold pt-2 border-t border-gray-100">
                  Cap: {sub.base_mailboxes + sub.extra_mailboxes} · Used: {data.mailbox_count ?? sub.mailbox_count ?? mailboxes.length}
                </div>
                {sub.current_period_end && (
                  <div className="text-xs text-gray-500">Period ends {fmtDateTime(sub.current_period_end)}</div>
                )}
                {sub.stripe_subscription_id && (
                  <div className="text-xs text-gray-500 font-mono pt-2 break-all">
                    Stripe sub: <a href={`https://dashboard.stripe.com/subscriptions/${sub.stripe_subscription_id}`} target="_blank" rel="noopener noreferrer" className="underline">{sub.stripe_subscription_id}</a>
                  </div>
                )}
              </div>
            </div>

            {/* Health */}
            {health && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-gray-500">Domain health</div>
                  <span className="text-xs text-gray-500">{new Date(health.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-primary-navy">
                  Registrar: {health.registrar || '—'} · Expires: {health.expires_at ? new Date(health.expires_at).toLocaleDateString() : '—'}
                </div>
                {health.reason && <div className="text-xs text-primary-navy/80 mt-1">{health.reason}</div>}
                {health.status_codes?.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1 font-mono">{health.status_codes.join(', ')}</div>
                )}
              </div>
            )}

            {/* Mailboxes (+ usage when the payload carries it) */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-gray-500">Mailboxes ({mailboxes.length} / {sub.base_mailboxes + sub.extra_mailboxes})</div>
                {usageRows.length > 0 && (
                  <div className="text-xs text-gray-500">
                    Storage: <span className="font-mono text-primary-navy">{fmtMb(usageTotals.used)}</span>
                    {usageTotals.quota > 0 && <> / {fmtMb(usageTotals.quota)} ({Math.min(100, Math.round((usageTotals.used / usageTotals.quota) * 100))}%)</>}
                  </div>
                )}
              </div>
              {mailboxes.length === 0 && usageRows.length === 0 ? (
                <div className="text-sm text-gray-500">No mailboxes created yet.</div>
              ) : (
                <ul className="text-sm space-y-2">
                  {(mailboxes.length > 0 ? mailboxes : usageRows).map((m: any) => {
                    const u = usageFor(m);
                    const pct = u ? Math.max(0, Math.min(100, Number(u.used_pct) || (Number(u.quota_mb) > 0 ? Math.round((Number(u.used_mb) / Number(u.quota_mb)) * 100) : 0))) : 0;
                    return (
                      <li key={m.id ?? m.email}>
                        <div className="font-mono text-primary-navy break-all">
                          {m.email} {m.active === false && <span className="text-xs text-gray-500 font-sans">· suspended</span>}
                        </div>
                        {u && (
                          <div className="mt-1 grid grid-cols-1 sm:grid-cols-[minmax(0,220px)_1fr] gap-x-3 gap-y-0.5 items-center">
                            <div>
                              <div className="flex justify-between text-[11px] text-gray-600">
                                <span>{fmtMb(u.used_mb)}{Number(u.quota_mb) > 0 ? ` / ${fmtMb(u.quota_mb)}` : ''}</span>
                                <span className="font-mono">{pct}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded overflow-hidden" role="progressbar" aria-label={`Storage used by ${u.email || m.email}`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                <div className={`h-full ${pct >= 95 ? 'bg-primary-navy' : pct >= 80 ? 'bg-secondary-700' : 'bg-secondary-500'}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                            <div className="text-[11px] text-gray-600">
                              {Number.isFinite(Number(u.messages)) && <>{Number(u.messages).toLocaleString()} msgs · </>}
                              Last login: {u.last_login_at
                                ? <span title={fmtDateTime(u.last_login_at)}>{fmtRelative(u.last_login_at)}{u.last_login_ip ? ` · ${u.last_login_ip}` : ''}{u.last_login_protocol ? ` · ${String(u.last_login_protocol).toUpperCase()}` : ''}</span>
                                : 'never'}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Provisioning timeline */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Provisioning timeline</div>
              {events.length === 0 ? (
                <div className="text-sm text-gray-500">No events yet.</div>
              ) : (
                <ol className="text-sm space-y-2 max-h-72 overflow-y-auto">
                  {events.map((e: any) => (
                    <li key={e.id} className="flex items-start gap-2">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-none ${
                        e.severity === 'error' ? 'bg-primary-navy' :
                        e.severity === 'warning' ? 'bg-secondary-500' : 'bg-secondary-300'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-primary-navy">{e.message}</div>
                        <div className="text-xs text-gray-500">
                          {e.from_status && e.to_status ? `${e.from_status} → ${e.to_status} · ` : ''}
                          {new Date(e.created_at).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Tier editor ----

const TierEditor: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const closeEditor = useCallback(() => setEditing(null), []);
  useEscape(closeEditor);

  const load = useCallback(async () => {
    try {
      const r = await api('/admin/email/business-email/tiers');
      if (r.success) { setTiers(Array.isArray(r.data) ? r.data : []); setErr(null); }
      else setErr(r.error || 'Failed to load tiers');
    } catch (e: any) {
      setErr(e?.message || 'Failed to load tiers');
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api(`/admin/email/business-email/tiers/${editing.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          mailboxes_per_subscription: Number(editing.mailboxes_per_subscription),
          storage_per_mailbox_gb: Number(editing.storage_per_mailbox_gb),
          aliases_per_subscription: Number(editing.aliases_per_subscription),
          extra_mailbox_price_monthly: editing.extra_mailbox_price_monthly,
          extra_mailbox_price_yearly: editing.extra_mailbox_price_yearly,
          max_extra_mailboxes: editing.max_extra_mailboxes === null ? null : Number(editing.max_extra_mailboxes),
          price_monthly: editing.price_monthly,
          price_yearly: editing.price_yearly,
        }),
      });
      if (r.success) { setEditing(null); load(); onSaved(); }
      else setErr(r.error || 'Save failed');
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <h3 className="text-lg font-semibold text-primary-navy mb-3">Tier configuration</h3>
      <p className="text-xs text-gray-500 mb-3">
        Changes apply to <strong>future</strong> subscriptions only — existing customers keep the limits they signed up under.
      </p>
      {err && (
        <div role="alert" className="mb-3 p-3 bg-secondary-100 border border-secondary-500 rounded text-sm text-primary-navy">
          🚨 {err} <button type="button" onClick={load} className="underline ml-2">Retry</button>
        </div>
      )}
      {loaded && !err && tiers.length === 0 && (
        <div className="text-sm text-gray-500">No tiers configured.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {tiers.map(t => (
          <div key={t.id} className="border border-gray-200 rounded-lg p-3">
            <div className="font-semibold text-primary-navy">{t.name}</div>
            <div className="text-xs text-gray-500 mb-2">${t.price_monthly}/mo · ${t.price_yearly}/yr</div>
            <div className="text-sm space-y-0.5">
              <div>Mailboxes: <strong>{t.mailboxes_per_subscription}</strong></div>
              <div>Storage: <strong>{t.storage_per_mailbox_gb} GB</strong>/mailbox</div>
              <div>Aliases: <strong>{t.aliases_per_subscription === -1 ? 'unlimited' : t.aliases_per_subscription}</strong></div>
              <div>Extras: <strong>${t.extra_mailbox_price_monthly}</strong>/extra/mo</div>
              <div>Cap on extras: <strong>{t.max_extra_mailboxes === null ? 'unlimited' : t.max_extra_mailboxes}</strong></div>
            </div>
            <button type="button" onClick={() => setEditing({ ...t })}
              className="mt-3 text-xs px-3 py-1.5 bg-secondary-500 text-primary-navy font-medium rounded" aria-label={`Edit ${t.name}`}>
              Edit
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-primary-navy/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={closeEditor}>
          <div role="dialog" aria-modal="true" aria-labelledby="tier-edit-title" onClick={e => e.stopPropagation()} className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 id="tier-edit-title" className="text-lg font-semibold text-primary-navy">Edit {editing.name}</h3>
              <button type="button" onClick={closeEditor} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 text-sm">
              {([
                ['price_monthly', '$ Base monthly'],
                ['price_yearly', '$ Base yearly'],
                ['mailboxes_per_subscription', 'Base mailboxes'],
                ['storage_per_mailbox_gb', 'Storage/mailbox (GB)'],
                ['aliases_per_subscription', 'Aliases (-1 = unlimited)'],
                ['extra_mailbox_price_monthly', '$ Extra/mailbox/mo'],
                ['extra_mailbox_price_yearly', '$ Extra/mailbox/yr'],
                ['max_extra_mailboxes', 'Max extras (blank = unlimited)'],
              ] as const).map(([key, label]) => (
                <label key={key} className="block">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</div>
                  <input
                    value={(editing as any)[key] ?? ''}
                    onChange={e => setEditing(s => s ? { ...s, [key]: e.target.value === '' && key === 'max_extra_mailboxes' ? null : e.target.value } as Tier : s)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  />
                </label>
              ))}
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={closeEditor} className="text-sm px-4 py-2 border border-gray-300 rounded-lg text-primary-navy" disabled={busy}>Cancel</button>
              <button type="button" onClick={save} disabled={busy} className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Manual subscription creation ----

const ManualCreate: React.FC<{ onCreated: () => void }> = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [form, setForm] = useState({ customerId: '', packageId: '', domain: '', billingCycle: 'monthly' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const close = useCallback(() => setOpen(false), []);
  useEscape(close);

  useEffect(() => {
    if (!open) return;
    api('/admin/email/business-email/tiers')
      .then(r => { if (r.success) setTiers(Array.isArray(r.data) ? r.data : []); else setErr(r.error || 'Failed to load tiers'); })
      .catch(e => setErr(e?.message || 'Failed to load tiers'));
  }, [open]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api('/admin/email/business-email/subscriptions/manual', {
        method: 'POST', body: JSON.stringify(form),
      });
      if (!r.success) { setErr(r.error || 'Failed'); return; }
      setOpen(false); setForm({ customerId: '', packageId: '', domain: '', billingCycle: 'monthly' });
      onCreated();
    } catch (e: any) {
      setErr(e?.message || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition">
        + Create manual subscription
      </button>
      {open && (
        <div className="fixed inset-0 bg-primary-navy/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={close}>
          <div role="dialog" aria-modal="true" aria-labelledby="manual-sub-title" onClick={e => e.stopPropagation()} className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 id="manual-sub-title" className="text-lg font-semibold text-primary-navy">Manual subscription</h3>
              <button type="button" onClick={close} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="text-xs text-gray-600 bg-secondary-50 border border-secondary-200 rounded p-2">
                Comp / internal subscription — no Stripe billing. Use for employee accounts, free trials, or recovery scenarios.
              </div>
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Customer ID</div>
                <input value={form.customerId} onChange={e => setForm(s => ({ ...s, customerId: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-primary-navy font-mono" placeholder="UUID" />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Tier</div>
                <select value={form.packageId} onChange={e => setForm(s => ({ ...s, packageId: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-primary-navy">
                  <option value="">— pick a tier —</option>
                  {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Domain</div>
                <input value={form.domain} onChange={e => setForm(s => ({ ...s, domain: e.target.value.toLowerCase() }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-primary-navy font-mono" placeholder="example.com" />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Billing cycle</div>
                <select value={form.billingCycle} onChange={e => setForm(s => ({ ...s, billingCycle: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-primary-navy">
                  <option value="monthly">monthly</option>
                  <option value="yearly">yearly</option>
                </select>
              </label>
              {err && <div role="alert" className="text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded p-2">🚨 {err}</div>}
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={close} className="text-sm px-4 py-2 border border-gray-300 rounded-lg text-primary-navy" disabled={busy}>Cancel</button>
              <button type="button" onClick={submit} disabled={busy || !form.customerId || !form.packageId || !form.domain}
                className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg disabled:opacity-50">
                {busy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ---- Audit log ----

const AuditLog: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    api('/admin/email/business-email/audit-log')
      .then(r => { if (r.success) { setRows(Array.isArray(r.data) ? r.data : []); setErr(null); } else setErr(r.error || 'Failed to load audit log'); })
      .catch(e => setErr(e?.message || 'Failed to load audit log'))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <h3 className="text-lg font-semibold text-primary-navy mb-3">Audit log</h3>
      {err ? (
        <div role="alert" className="p-3 bg-secondary-100 border border-secondary-500 rounded text-sm text-primary-navy">
          🚨 {err} <button type="button" onClick={load} className="underline ml-2">Retry</button>
        </div>
      ) : !loaded ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500">No admin actions yet.</div>
      ) : (
        <ol className="text-sm space-y-2">
          {rows.map(r => (
            <li key={r.id} className="border-b border-gray-100 pb-2">
              <div className="text-primary-navy">{r.message}</div>
              <div className="text-xs text-gray-500">
                {r.admin_email || 'system'} · {r.action} · {new Date(r.created_at).toLocaleString()}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// ---- Main component ----

const BusinessEmailManagement: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  // Debounced copy of `search` — the list only refetches 300ms after typing stops.
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'subs' | 'tiers' | 'audit'>('subs');
  const [listErr, setListErr] = useState<string | null>(null);
  const [listLoaded, setListLoaded] = useState(false);
  // Monotonic request counter so a slow earlier response can't overwrite a
  // newer one (out-of-order protection).
  const reqSeq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    const qs = [
      statusFilter && `status=${encodeURIComponent(statusFilter)}`,
      query && `q=${encodeURIComponent(query)}`,
    ].filter(Boolean).join('&');
    const [s, l] = await Promise.allSettled([
      api('/admin/email/business-email/stats'),
      api(`/admin/email/business-email/subscriptions${qs ? `?${qs}` : ''}`),
    ]);
    if (seq !== reqSeq.current) return; // a newer request superseded this one
    if (s.status === 'fulfilled' && s.value.success) setStats(s.value.data);
    if (l.status === 'fulfilled') {
      if (l.value.success) { setSubs(Array.isArray(l.value.data) ? l.value.data : []); setListErr(null); }
      else setListErr(l.value.error || 'Failed to load subscriptions');
    } else {
      setListErr((l.reason as any)?.message || 'Failed to load subscriptions');
    }
    setListLoaded(true);
  }, [statusFilter, query]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-r from-primary-navy via-primary-700 to-secondary-500 p-6 text-white shadow">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold">Business Email</h2>
            <p className="text-sm text-white/90 mt-1">
              Subscriptions, tiers, provisioning health, and admin actions for the /email product.
            </p>
          </div>
          <ManualCreate onCreated={load} />
        </div>
      </div>

      <StatsHeader stats={stats} />

      <div className="flex gap-2 border-b border-gray-200">
        {([
          ['subs', 'Subscriptions'],
          ['tiers', 'Tier config'],
          ['audit', 'Audit log'],
        ] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
              tab === k
                ? 'border-secondary-500 text-primary-navy font-semibold'
                : 'border-transparent text-gray-500 hover:text-primary-navy'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'subs' && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by domain or customer email"
              aria-label="Search subscriptions by domain or customer email"
              type="search"
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-primary-navy" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              className="px-3 py-2 border border-gray-300 rounded-lg text-primary-navy">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending_provisioning">Provisioning</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {listErr ? (
            <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-xl p-6 text-center text-primary-navy">
              🚨 {listErr} <button type="button" onClick={load} className="underline ml-2">Retry</button>
            </div>
          ) : !listLoaded ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
              {query || statusFilter ? 'No subscriptions match this filter.' : 'No business-email subscriptions yet.'}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left">Domain</th>
                    <th scope="col" className="px-4 py-2 text-left">Customer</th>
                    <th scope="col" className="px-4 py-2 text-left">Tier</th>
                    <th scope="col" className="px-4 py-2 text-left">Seats</th>
                    <th scope="col" className="px-4 py-2 text-left">Status</th>
                    <th scope="col" className="px-4 py-2 text-left">Pipeline</th>
                    <th scope="col" className="px-4 py-2 text-left">Renews</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open ${s.domain}`}
                      onClick={() => setSelected(s.id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(s.id); } }}
                      className="border-t border-gray-100 hover:bg-secondary-50 focus:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary-500 cursor-pointer">
                      <td className="px-4 py-2 font-mono text-primary-navy">
                        {s.domain}
                        {healthChip(s.health_severity, s.health_reason)}
                      </td>
                      <td className="px-4 py-2 text-primary-navy">
                        <div>{s.customer_name || '—'}</div>
                        <div className="text-xs text-gray-500">{s.customer_email}</div>
                      </td>
                      <td className="px-4 py-2 text-primary-navy">{s.package_name}</td>
                      <td className="px-4 py-2 text-primary-navy tabular-nums">
                        {s.base_mailboxes + (s.extra_mailboxes || 0)}
                      </td>
                      <td className="px-4 py-2">
                        {statusChip(s.status)}
                        {s.status === 'suspended' && s.suspended_reason && (
                          <div className="text-xs text-gray-500 mt-0.5 max-w-[14rem] truncate" title={s.suspended_reason}>{s.suspended_reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 font-mono">
                        {s.mx1_row_id ? (
                          <>
                            <div title="mx1 provisioning status">mx1: {s.mx1_status || '—'}</div>
                            {s.ses_identity_status && <div title="SES identity status">ses: {s.ses_identity_status}</div>}
                            <div title="Welcome email">{s.welcome_email_sent_at ? '✉ welcome sent' : '✉ no welcome'}</div>
                            {s.mx1_last_error && <div className="text-primary-navy max-w-[14rem] truncate" title={s.mx1_last_error}>⚠ {s.mx1_last_error}</div>}
                          </>
                        ) : s.customer_mail_domain_id ? (
                          <>
                            <div title="v1 provisioning status">cmd: {s.cmd_status || '—'}</div>
                            {s.cmd_last_error && <div className="text-primary-navy max-w-[14rem] truncate" title={s.cmd_last_error}>⚠ {s.cmd_last_error}</div>}
                          </>
                        ) : (
                          <span title="No mail-domain row yet">— no row</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'tiers' && <TierEditor onSaved={load} />}
      {tab === 'audit' && <AuditLog />}

      {selected && (
        <DetailDrawer subId={selected} onClose={() => setSelected(null)} onChanged={load} />
      )}
    </div>
  );
};

export default BusinessEmailManagement;
